/**
 * Book vs FMV "Suggested action" kernel + its basis seam (spec §4
 * "By-Asset-Class table"; basis disclosure per spec Cross-cutting).
 *
 * This used to be a private helper inside `apps/customer/.../book-vs-fmv.tsx`
 * whose own comment conceded it was "a UI-only heuristic, not a
 * stored/computed value" — and it was the one element on the screen that
 * recommends an *action* while carrying no basis at all, three cards above an
 * impairment watchlist engineered specifically to avoid appearing to make a
 * determination. It lives here now so that:
 *
 *   1. the bands are registered constants (`financials-constants.ts`), not
 *      literals in a `.tsx`, and are therefore emittable inside
 *      `CalculationBasis.constants[]`;
 *   2. `describeSuggestedActionBasis` sits beside the kernel it documents,
 *      per spec Cross-cutting ("formulaId ... defined beside the kernel it
 *      names") — same assembly seam as `describeMarketTrendsBasis`;
 *   3. the method is testable without React.
 *
 * **The method is deliberately still weak, and the basis says so.** It
 * compares the FIRST and LAST point of the 18-month trend — two sampled
 * endpoints, not a fitted regression — so a class that crashed and fully
 * recovered reads "stable", and the band edges are cliffs. That is the fact a
 * board member will ask about, so `trendPointsAvailable` is disclosed
 * alongside the two points actually used and both endpoints are flagged
 * `isFallback: true`. Replacing this with a server-side trailing slope over
 * gap %, quarters-below, ITAD delta and lifecycle stage is the real fix; until
 * then the figure is auditable rather than silent.
 *
 * No DB access, no async, no Date.now() here — `asOf` comes off each trend
 * point's own `month`.
 */

import {
  SUGGESTED_ACTION_MONITOR_PCT,
  SUGGESTED_ACTION_RISING_PCT,
  SUGGESTED_ACTION_SELL_SOON_PCT,
} from './financials-constants.js';
import type { CalculationBasisInput, FmvTrendPoint } from './dto/financials.js';

/** `formulaId` for the suggested-action band — named for what it actually is
 * (a two-point endpoint comparison), so the drawer cannot imply a trend fit. */
export const SUGGESTED_ACTION_FORMULA_ID = 'book_vs_fmv.suggested_action.two_point_endpoints.v1';

export type SuggestedActionTone = 'positive' | 'negative' | 'muted';

export interface SuggestedAction {
  label: string;
  tone: SuggestedActionTone;
  /** Percent change between the two endpoints, or null when the trend has
   * fewer than two points or starts at zero (band resolves to "Monitor"). */
  changePct: number | null;
}

/**
 * Percent change between the first and last trend point. Null when there is
 * nothing to compare (0 or 1 point) or the first point is zero, so the caller
 * never divides by zero and never reports a change it cannot support.
 *
 * @pure
 */
export function trendEndpointChangePct(trend: readonly FmvTrendPoint[]): number | null {
  const first = trend[0];
  const last = trend[trend.length - 1];
  if (!first || !last || trend.length < 2 || first.fmvMedUsd === 0) return null;
  return ((last.fmvMedUsd - first.fmvMedUsd) / first.fmvMedUsd) * 100;
}

/** @pure */
export function suggestedActionFromTrend(trend: readonly FmvTrendPoint[]): SuggestedAction {
  const changePct = trendEndpointChangePct(trend);
  if (changePct === null) return { label: 'Monitor', tone: 'muted', changePct };
  if (changePct <= SUGGESTED_ACTION_SELL_SOON_PCT) {
    return { label: 'Sell soon — value eroding', tone: 'negative', changePct };
  }
  if (changePct <= SUGGESTED_ACTION_MONITOR_PCT) {
    return { label: 'Monitor closely', tone: 'negative', changePct };
  }
  if (changePct >= SUGGESTED_ACTION_RISING_PCT) {
    return { label: 'Hold — FMV rising', tone: 'positive', changePct };
  }
  return { label: 'Hold — FMV stable', tone: 'positive', changePct };
}

/**
 * Names the four values the band is built from. Both endpoints carry
 * `isFallback: true` — they are two samples standing in for eighteen months
 * of movement, which is the candid-fallback rule applied to a method rather
 * than to a missing datum.
 *
 * `source` is `fmv_snapshot` throughout because that is genuinely where the
 * trend points come from (`fmv_pricing_snapshot`); the closed `BasisSource`
 * enum has no "derived in the browser" member and inventing one would be
 * stringly-typing it. The drawer distinguishes these inputs by NAME to render
 * method-specific copy.
 *
 * @pure
 */
export function describeSuggestedActionBasis(
  trend: readonly FmvTrendPoint[],
): CalculationBasisInput[] {
  const first = trend[0];
  const last = trend[trend.length - 1];
  const changePct = trendEndpointChangePct(trend);
  const unknownAsOf = '';

  return [
    {
      name: 'trendPointsAvailable',
      value: trend.length,
      source: 'fmv_snapshot',
      asOf: last?.month ?? unknownAsOf,
      isFallback: false,
    },
    {
      name: 'trendStartFmvMedUsd',
      value: first?.fmvMedUsd ?? null,
      source: 'fmv_snapshot',
      asOf: first?.month ?? unknownAsOf,
      isFallback: true,
    },
    {
      name: 'trendEndFmvMedUsd',
      value: last?.fmvMedUsd ?? null,
      source: 'fmv_snapshot',
      asOf: last?.month ?? unknownAsOf,
      isFallback: true,
    },
    {
      name: 'trendChangePct',
      value: changePct === null ? null : Number(changePct.toFixed(1)),
      source: 'fmv_snapshot',
      asOf: last?.month ?? unknownAsOf,
      isFallback: true,
    },
  ];
}
